import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import Input from '../Common/Input';
import styles from './ConfigurationPanel.module.css';

export interface ConfigurationPanelProps {
  enhancedCharacter: EnhancedCharacter;
  showDebugStats?: boolean;
}

/**
 * ConfigurationPanel - Collapsible panel for max values and debug info (TERTIARY)
 *
 * Shows:
 * - Max value inputs (HP, Armor, Hope, Stress, Evasion, Thresholds)
 * - Computed stats debug information (in development mode)
 *
 * This is TERTIARY information - configuration and debug data.
 * State persists to localStorage.
 */
export const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  enhancedCharacter,
  showDebugStats = false,
}) => {
  const { character, computedStats, statComputer } = enhancedCharacter;

  // Initialize from localStorage or default to collapsed
  const [isExpanded, setIsExpanded] = useState(() => {
    const stored = localStorage.getItem('combat-config-expanded');
    return stored === 'true';
  });

  // Persist expansion state to localStorage
  useEffect(() => {
    localStorage.setItem('combat-config-expanded', String(isExpanded));
  }, [isExpanded]);

  if (!character || !computedStats) {
    return null;
  }

  // Get pure computed stats (before manual overrides) for reset buttons
  // Falls back to computedStats if pureComputedStats is not available
  const pureComputedStats = statComputer?.getPureComputedStats() || computedStats;

  // Helper to get effective value (manual override or computed)
  type OverridableField = 'evasion' | 'armor' | 'hpSlots' | 'stressSlots' | 'hope';

  const getEffectiveValue = (field: OverridableField): number => {
    const override = character.manualOverrides?.[field];
    return override !== undefined ? override : computedStats[field];
  };

  const getEffectiveThreshold = (type: 'major' | 'severe'): number => {
    const overrideField = type === 'major' ? 'thresholdMajor' : 'thresholdSevere';
    const override = character.manualOverrides?.[overrideField];
    return override !== undefined ? override : computedStats.thresholds[type];
  };

  // Check if value is manually overridden
  const isOverridden = (field: OverridableField): boolean => {
    return character.manualOverrides?.[field] !== undefined;
  };

  const isThresholdOverridden = (type: 'major' | 'severe'): boolean => {
    const overrideField = type === 'major' ? 'thresholdMajor' : 'thresholdSevere';
    return character.manualOverrides?.[overrideField] !== undefined;
  };

  // Manual override handlers (use updateManualOverrides instead of updateBaseStats)
  const handleMaxHpChange = (value: string) => {
    const newMax = Math.max(0, parseInt(value) || 0);
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({ hpSlots: newMax });
    }
  };

  const handleMaxArmorChange = (value: string) => {
    const newMax = Math.max(0, parseInt(value) || 0);
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({ armor: newMax });
    }
  };

  const handleMaxHopeChange = (value: string) => {
    const newMax = Math.max(0, parseInt(value) || 0);
    if (enhancedCharacter.updateManualOverrides && enhancedCharacter.updateCurrentState) {
      enhancedCharacter.updateManualOverrides({ hope: newMax });
      // Clamp current hope to new max
      enhancedCharacter.updateCurrentState({
        currentHope: Math.min(character.currentState.currentHope, newMax),
      });
    }
  };

  const handleMaxStressChange = (value: string) => {
    const newMax = Math.max(0, parseInt(value) || 0);
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({ stressSlots: newMax });
    }
  };

  const handleEvasionChange = (value: string) => {
    if (enhancedCharacter.updateManualOverrides) {
      const parsed = parseInt(value);
      const newValue = isNaN(parsed) ? 11 : Math.max(0, parsed);
      enhancedCharacter.updateManualOverrides({ evasion: newValue });
    }
  };

  const handleMajorThresholdChange = (value: string) => {
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({
        thresholdMajor: parseInt(value) || 0,
      });
    }
  };

  const handleSevereThresholdChange = (value: string) => {
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({
        thresholdSevere: parseInt(value) || 0,
      });
    }
  };

  // Reset handlers (set override to undefined to revert to computed)
  const handleResetMaxHp = () => {
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({ hpSlots: undefined });
    }
  };

  const handleResetMaxArmor = () => {
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({ armor: undefined });
    }
  };

  const handleResetMaxHope = () => {
    if (enhancedCharacter.updateManualOverrides && enhancedCharacter.updateCurrentState) {
      enhancedCharacter.updateManualOverrides({ hope: undefined });
      // Clamp current hope to pure computed max
      enhancedCharacter.updateCurrentState({
        currentHope: Math.min(character.currentState.currentHope, pureComputedStats.hope),
      });
    }
  };

  const handleResetMaxStress = () => {
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({ stressSlots: undefined });
    }
  };

  const handleResetEvasion = () => {
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({ evasion: undefined });
    }
  };

  const handleResetMajorThreshold = () => {
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({
        thresholdMajor: undefined,
      });
    }
  };

  const handleResetSevereThreshold = () => {
    if (enhancedCharacter.updateManualOverrides) {
      enhancedCharacter.updateManualOverrides({
        thresholdSevere: undefined,
      });
    }
  };

  return (
    <div className={styles.configurationPanel}>
      <button
        className={styles.configurationPanel__toggle}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="combat-config-content"
      >
        <span className={styles.configurationPanel__toggleIcon}>⚙️</span>
        <span className={styles.configurationPanel__toggleText}>
          Configure Max Values
        </span>
        <span
          className={`${styles.configurationPanel__toggleChevron} ${
            isExpanded ? styles['configurationPanel__toggleChevron--expanded'] : ''
          }`}
        >
          ▼
        </span>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id="combat-config-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={styles.configurationPanel__content}
          >
            <div className={styles.configurationPanel__form}>
              {/* Combat Values */}
              <div className={styles.configurationPanel__section}>
                <h3 className={styles.configurationPanel__sectionTitle}>
                  Combat Values
                </h3>
                <div className={styles.configurationPanel__grid}>
                  <div className={styles.configurationPanel__field}>
                    <Input
                      label="Max HP"
                      type="NUMBER"
                      value={String(getEffectiveValue('hpSlots'))}
                      onChange={handleMaxHpChange}
                      min={0}
                    />
                    {isOverridden('hpSlots') && (
                      <button
                        className={styles.configurationPanel__resetButton}
                        onClick={handleResetMaxHp}
                        title={`Reset to computed value (${pureComputedStats.hpSlots})`}
                      >
                        Reset ({pureComputedStats.hpSlots})
                      </button>
                    )}
                  </div>
                  <div className={styles.configurationPanel__field}>
                    <Input
                      label="Max Armor"
                      type="NUMBER"
                      value={String(getEffectiveValue('armor'))}
                      onChange={handleMaxArmorChange}
                      min={0}
                    />
                    {isOverridden('armor') && (
                      <button
                        className={styles.configurationPanel__resetButton}
                        onClick={handleResetMaxArmor}
                        title={`Reset to computed value (${pureComputedStats.armor})`}
                      >
                        Reset ({pureComputedStats.armor})
                      </button>
                    )}
                  </div>
                  <div className={styles.configurationPanel__field}>
                    <Input
                      label="Max Hope"
                      type="NUMBER"
                      value={String(getEffectiveValue('hope'))}
                      onChange={handleMaxHopeChange}
                      min={0}
                    />
                    {isOverridden('hope') && (
                      <button
                        className={styles.configurationPanel__resetButton}
                        onClick={handleResetMaxHope}
                        title={`Reset to computed value (${pureComputedStats.hope})`}
                      >
                        Reset ({pureComputedStats.hope})
                      </button>
                    )}
                  </div>
                  <div className={styles.configurationPanel__field}>
                    <Input
                      label="Max Stress"
                      type="NUMBER"
                      value={String(getEffectiveValue('stressSlots'))}
                      onChange={handleMaxStressChange}
                      min={0}
                    />
                    {isOverridden('stressSlots') && (
                      <button
                        className={styles.configurationPanel__resetButton}
                        onClick={handleResetMaxStress}
                        title={`Reset to computed value (${pureComputedStats.stressSlots})`}
                      >
                        Reset ({pureComputedStats.stressSlots})
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Defensive & Thresholds */}
              <div className={styles.configurationPanel__section}>
                <h3 className={styles.configurationPanel__sectionTitle}>
                  Defensive & Thresholds
                </h3>
                <div className={styles.configurationPanel__grid}>
                  <div className={styles.configurationPanel__field}>
                    <Input
                      label="Evasion"
                      type="NUMBER"
                      value={String(getEffectiveValue('evasion'))}
                      onChange={handleEvasionChange}
                      min={0}
                    />
                    {isOverridden('evasion') && (
                      <button
                        className={styles.configurationPanel__resetButton}
                        onClick={handleResetEvasion}
                        title={`Reset to computed value (${pureComputedStats.evasion})`}
                      >
                        Reset ({pureComputedStats.evasion})
                      </button>
                    )}
                  </div>
                  <div className={styles.configurationPanel__field}>
                    <Input
                      label="Major Threshold"
                      type="NUMBER"
                      value={String(getEffectiveThreshold('major'))}
                      onChange={handleMajorThresholdChange}
                      min={0}
                    />
                    {isThresholdOverridden('major') && (
                      <button
                        className={styles.configurationPanel__resetButton}
                        onClick={handleResetMajorThreshold}
                        title={`Reset to computed value (${pureComputedStats.thresholds.major})`}
                      >
                        Reset ({pureComputedStats.thresholds.major})
                      </button>
                    )}
                  </div>
                  <div className={styles.configurationPanel__field}>
                    <Input
                      label="Severe Threshold"
                      type="NUMBER"
                      value={String(getEffectiveThreshold('severe'))}
                      onChange={handleSevereThresholdChange}
                      min={0}
                    />
                    {isThresholdOverridden('severe') && (
                      <button
                        className={styles.configurationPanel__resetButton}
                        onClick={handleResetSevereThreshold}
                        title={`Reset to computed value (${pureComputedStats.thresholds.severe})`}
                      >
                        Reset ({pureComputedStats.thresholds.severe})
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Debug Stats (Development Only) */}
              {showDebugStats && (
                <div className={styles.configurationPanel__section}>
                  <h3 className={styles.configurationPanel__sectionTitle}>
                    Computed Stats (Debug)
                  </h3>
                  <div className={styles.configurationPanel__debugGrid}>
                    <div className={styles.configurationPanel__debugRow}>
                      <span className={styles.configurationPanel__debugLabel}>
                        Computed HP:
                      </span>
                      <span className={styles.configurationPanel__debugValue}>
                        {computedStats.hpSlots}
                      </span>
                    </div>
                    <div className={styles.configurationPanel__debugRow}>
                      <span className={styles.configurationPanel__debugLabel}>
                        Computed Armor:
                      </span>
                      <span className={styles.configurationPanel__debugValue}>
                        {computedStats.armor}
                      </span>
                    </div>
                    <div className={styles.configurationPanel__debugRow}>
                      <span className={styles.configurationPanel__debugLabel}>
                        Computed Stress:
                      </span>
                      <span className={styles.configurationPanel__debugValue}>
                        {computedStats.stressSlots}
                      </span>
                    </div>
                    <div className={styles.configurationPanel__debugRow}>
                      <span className={styles.configurationPanel__debugLabel}>
                        Computed Evasion:
                      </span>
                      <span className={styles.configurationPanel__debugValue}>
                        {computedStats.evasion}
                      </span>
                    </div>
                    <div className={styles.configurationPanel__debugRow}>
                      <span className={styles.configurationPanel__debugLabel}>
                        Major Threshold:
                      </span>
                      <span className={styles.configurationPanel__debugValue}>
                        {computedStats.thresholds.major}
                      </span>
                    </div>
                    <div className={styles.configurationPanel__debugRow}>
                      <span className={styles.configurationPanel__debugLabel}>
                        Severe Threshold:
                      </span>
                      <span className={styles.configurationPanel__debugValue}>
                        {computedStats.thresholds.severe}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConfigurationPanel;
